import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import config from "../../config";
import makeRequest from "../../makeRequest";

/* ─── Injected styles ───────────────────────────────────────────────────── */
const STYLE_ID = "dept-listing-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes deptListFadeUp {
      from { opacity: 0; transform: translateY(32px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes deptListShimmer {
      0%   { background-position: -600px 0; }
      100% { background-position: 600px 0; }
    }

    .dept-listing-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 32px;
    }
    @media (max-width: 991px) {
      .dept-listing-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
    }
    @media (max-width: 580px) {
      .dept-listing-grid { grid-template-columns: 1fr; gap: 20px; }
    }

    .dept-card {
      background: #fff;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 4px 22px rgba(8, 87, 222, 0.09);
      transition: transform 0.28s ease, box-shadow 0.28s ease;
      cursor: pointer;
      display: flex;
      flex-direction: column;
    }
    .dept-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 14px 36px rgba(8, 87, 222, 0.16);
    }
    .dept-card-thumb {
      position: relative;
      overflow: hidden;
      background: #edf5fc;
    }
    .dept-card-thumb img {
      width: 100%;
      height: 220px;
      object-fit: cover;
      object-position: center;
      display: block;
      transition: transform 1.2s ease;
    }
    .dept-card:hover .dept-card-thumb img {
      transform: scale(1.05);
    }
    .dept-card-body {
      padding: 26px 24px 28px;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .dept-card-name {
      font-size: 18px;
      font-weight: 700;
      color: #0f172a;
      margin: 0 0 10px;
      line-height: 1.3;
    }
    .dept-card-desc {
      font-size: 14px;
      color: #64748b;
      line-height: 1.7;
      margin: 0 0 20px;
      flex: 1;
    }
    .dept-card-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      color: #1b74bb;
      background: #edf5fc;
      border: none;
      border-radius: 6px;
      padding: 8px 16px;
      cursor: pointer;
      transition: background 0.2s;
      text-decoration: none;
      align-self: flex-start;
    }
    .dept-card-btn:hover {
      background: #d0e8f8;
      color: #0d5a94;
    }
  `;
  document.head.appendChild(style);
}

/* ─── Icon placeholder (when no image) ─────────────────────────────────── */
function DeptPlaceholder({ name }) {
  const letters = (name || "?")
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <div
      style={{
        width: "100%",
        height: 220,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #1b74bb22, #edf5fc)",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          background: "linear-gradient(145deg, #1b74bb, #0d3b6e)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          fontWeight: 800,
          color: "#fff",
          letterSpacing: 1,
        }}
      >
        {letters}
      </div>
      <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>{name}</span>
    </div>
  );
}

/* ─── Image fetcher ─────────────────────────────────────────────────────── */
function useBlobImage(url) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!url) {
      setLoading(false);
      return;
    }
    let objectUrl = null;
    setLoading(true);
    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => setSrc(null))
      .finally(() => setLoading(false));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return { src, loading };
}

/* ─── Department Card ───────────────────────────────────────────────────── */
function DeptCard({ dept, delay }) {
  const baseUrl = config.api.baseUrl;
  const { src, loading: imgLoading } = useBlobImage(dept.hasImage ? `${baseUrl}/website/department/${dept.id}/image` : null);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        animation: visible ? `deptListFadeUp 0.55s ${delay}s ease both` : "none",
      }}
    >
      <div
        className="dept-card"
        onClick={() => navigate(`/department/${dept.id}`)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && navigate(`/department/${dept.id}`)}
      >
        <div className="dept-card-thumb">
          {imgLoading ? (
            <div
              style={{
                width: "100%",
                height: 220,
                background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%)",
                backgroundSize: "800px 100%",
                animation: "deptListShimmer 1.6s infinite linear",
              }}
            />
          ) : src ? (
            <img src={src} alt={dept.name} />
          ) : (
            <DeptPlaceholder name={dept.name} />
          )}
        </div>

        <div className="dept-card-body">
          <h3 className="dept-card-name">{dept.name}</h3>
          {dept.description && (
            <p className="dept-card-desc">{dept.description.length > 120 ? `${dept.description.slice(0, 120)}…` : dept.description}</p>
          )}
          <span className="dept-card-btn">
            Learn More
            <i className="fa-solid fa-arrow-right" style={{ fontSize: 12 }} />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── Skeleton ──────────────────────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 4px 22px rgba(8,87,222,0.08)",
      }}
    >
      <div
        style={{
          width: "100%",
          height: 220,
          background: "linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%)",
          backgroundSize: "800px 100%",
          animation: "deptListShimmer 1.6s infinite linear",
        }}
      />
      <div style={{ padding: "26px 24px 28px" }}>
        <div
          style={{
            height: 22,
            width: "60%",
            background: "#e2e8f0",
            borderRadius: 6,
            marginBottom: 12,
          }}
        />
        <div
          style={{
            height: 14,
            width: "90%",
            background: "#f1f5f9",
            borderRadius: 5,
            marginBottom: 8,
          }}
        />
        <div
          style={{
            height: 14,
            width: "75%",
            background: "#f1f5f9",
            borderRadius: 5,
            marginBottom: 20,
          }}
        />
        <div
          style={{
            height: 34,
            width: 110,
            background: "#e2e8f0",
            borderRadius: 6,
          }}
        />
      </div>
    </div>
  );
}

/* ═══ MAIN ══════════════════════════════════════════════════════════════════ */
const DepartmentMain = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    makeRequest({ route: "/website/department/public", method: "get" })
      .then((res) => setDepartments(res?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main>
      <Breadcrumb title="Departments" subTitle="Departments" />

      <section style={{ paddingTop: 80, paddingBottom: 90, background: "#f8fafc" }}>
        <div className="container">
          {/* Section heading */}
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <span
              style={{
                display: "inline-block",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: "uppercase",
                color: "#1b74bb",
                marginBottom: 14,
              }}
            >
              Our Departments
            </span>
            <h2
              style={{
                fontSize: "clamp(26px, 3.5vw, 40px)",
                fontWeight: 800,
                color: "#0f172a",
                margin: "0 0 16px",
                lineHeight: 1.2,
              }}
            >
              Medical Departments
            </h2>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 5,
                marginBottom: 18,
              }}
            >
              <div style={{ width: 48, height: 4, borderRadius: 2, background: "#1b74bb" }} />
              <div style={{ width: 14, height: 4, borderRadius: 2, background: "#1b74bb80" }} />
              <div style={{ width: 6, height: 4, borderRadius: 2, background: "#1b74bb30" }} />
            </div>
            <p
              style={{
                margin: "0 auto",
                maxWidth: 560,
                fontSize: 15,
                color: "#64748b",
                lineHeight: 1.8,
              }}
            >
              Explore our specialised departments staffed by expert medical professionals.
            </p>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="dept-listing-grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : departments.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: "60px 0", fontSize: 16 }}>No departments available at this time.</div>
          ) : (
            <div className="dept-listing-grid">
              {departments.map((dept, idx) => (
                <DeptCard key={dept.id} dept={dept} delay={Math.min(idx % 3, 2) * 0.1} />
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default DepartmentMain;
